import React from 'react';
import PropTypes from 'prop-types';
import { Field, FieldArray } from 'redux-form';

import { schedulesClassesPropTypes, schedulesInstructorsPropTypes } from 'constants/propTypes';
import { Card, CardSection } from 'components/Card';
import ComponentErrorBoundary from 'components/ComponentErrorBoundary';
import { DayOfWeekPicker, InputLabel } from 'components/Form';
import LoadingDots from 'components/LoadingDots';
import Message from 'components/Message';
import SvgQuestion from 'components/Svgs/Question';
import { TooltipToggle, TooltipContent } from 'components/Tooltip';
import { BackNavLink, Title } from 'features/App';
import { bem } from 'helpers/class-name';
import {
  SelectInstructorInput,
  SelectClassInput,
  CapacityInput,
  StartTimeDurationInput,
  ScheduleDateInputs,
  ScheduleTypeSelector,
  SingleDayExceptions,
} from '../ScheduleFormInputs';
import ScheduleFormFooter from '../ScheduleFormFooter/ScheduleFormFooterContainer';
import messages from '../ScheduleForm.messages';

const { block, element } = bem('schedule-form');

const AddScheduleForm = ({
  classes,
  currentFirstDuration,
  handleSubmit,
  instructors,
  isLoading,
  isNoEndDate,
  isRecurring,
  nextSingleDayExceptionDate,
  saveAsDraft,
  saveAsPublished,
}) => {
  if (isLoading) {
    return (
      <div className={block()}>
        <LoadingDots />
      </div>
    );
  }
  return (
    <>
      <BackNavLink useHistoryHook />
      <Message as={Title} message={messages.title} />
      <ComponentErrorBoundary>
        <Card className={block()} flush>
          <CardSection>
            <div className="grid">
              <div className="grid__item lg-1/2">
                <div className="grid">
                  <div className="grid__item">
                    <ScheduleTypeSelector />
                  </div>
                  <div className="grid__item mt-l">
                    <ScheduleDateInputs isRecurring={isRecurring} isNoEndDate={isNoEndDate} />
                  </div>
                  {isRecurring && (
                    <div className="grid__item mt-l" data-cypress="repeat-on-input">
                      <InputLabel message={messages.repeatOn} />
                      <Field name="recurrence" component={DayOfWeekPicker} />
                    </div>
                  )}
                  <div className="grid__item mt-l">
                    <FieldArray
                      name="startAndDuration"
                      component={StartTimeDurationInput}
                      currentFirstDuration={currentFirstDuration}
                    />
                  </div>
                  {isRecurring && (
                    <div className="grid__item mt-l">
                      <FieldArray
                        name="singleDayExceptions"
                        component={SingleDayExceptions}
                        nextSingleDayExceptionDate={nextSingleDayExceptionDate}
                      />
                    </div>
                  )}
                </div>
              </div>
              <div className="grid__item lg-1/2">
                <div className="grid">
                  <div className="grid__item" data-cypress="class-input">
                    <InputLabel message={messages.class} />
                    <Field name="classId" component={SelectClassInput} classes={classes} />
                  </div>
                  <div className="grid__item mt-l" data-cypress="instructor-input">
                    <InputLabel message={messages.instructor} optional />
                    <Field name="instructorId" component={SelectInstructorInput} instructors={instructors} />
                  </div>
                  <div className="grid__item 1/2 mt-l" data-cypress="capacity-input">
                    <div className={element('capacity-label-tooltip-flex-container')}>
                      <InputLabel className={element('capacity-label')} message={messages.capacity} optional />
                      <TooltipToggle name="tooltip-schedule-capacity">
                        <SvgQuestion className={element('tooltip-question-mark', 'ml-')} />
                      </TooltipToggle>
                      <TooltipContent name="tooltip-schedule-capacity" place="right" hoverable>
                        <Message message={messages.capacityTooltip} />
                      </TooltipContent>
                    </div>
                    <Field name="capacity" component={CapacityInput} />
                  </div>
                </div>
              </div>
            </div>
          </CardSection>

          <ScheduleFormFooter saveAsDraft={handleSubmit(saveAsDraft)} saveAsPublished={handleSubmit(saveAsPublished)} />
        </Card>
      </ComponentErrorBoundary>
    </>
  );
};

AddScheduleForm.propTypes = {
  handleSubmit: PropTypes.func,
  saveAsDraft: PropTypes.func,
  saveAsPublished: PropTypes.func,
  isLoading: PropTypes.bool,
  classes: schedulesClassesPropTypes,
  instructors: schedulesInstructorsPropTypes,
  isNoEndDate: PropTypes.bool,
  isRecurring: PropTypes.bool,
  currentFirstDuration: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  nextSingleDayExceptionDate: PropTypes.instanceOf(Date),
};

export default AddScheduleForm;
